/**
 * emailTypoGuard — yaygın email domain/TLD parmak-kayması yakalar, düzeltme önerir.
 * =================================================================================
 * Lead kaybı önler: kullanıcı "gmmail.com" yazınca onay/davet maili boşa gider,
 * kimse fark etmez, hesap açılmaz. Bu helper blur'da "gmail.com mı?" önerir.
 *
 * SSoT — TÜM email form'larında (lead modal, register, login...) kullanılır.
 * Frontend-only, ağ yok, sadece string analizi. Yanlış-pozitif düşük (sadece
 * bilinen typo'lar düzeltilir; tanımadığı domain'e dokunmaz).
 *
 * Ali 2026-06-26: "form gelmedi" vakalarının gerçek kökü çift-m typo (gmmail) çıktı.
 */

// Yaygın domain parmak-kayması → doğru domain (tam eşleşme)
const DOMAIN_FIXES: Record<string, string> = {
  // gmail
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmmail.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.cim': 'gmail.com',
  // hotmail
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  // outlook
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'outlook.co': 'outlook.com',
  // yahoo
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  // icloud
  'icloud.con': 'icloud.com',
  'iclould.com': 'icloud.com',
  'icloud.co': 'icloud.com',
  // yandex (TR yaygın)
  'yandex.con': 'yandex.com',
  'yandex.co': 'yandex.com',
};

// TLD typo düzeltmesini SADECE bu popüler domain'lerde uygula (yanlış-pozitif önler)
const POPULAR_DOMAINS = new Set([
  'gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com',
  'icloud.com', 'yandex.com', 'live.com', 'windowslive.com',
]);

/**
 * Email'de olası typo varsa düzeltilmiş halini döndürür, yoksa null.
 * @param {string} email
 * @returns {string|null} önerilen düzeltme (örn "ali@gmail.com") veya null
 */
export function suggestEmailFix(email: string): string | null {
  if (!email || typeof email !== 'string') return null;
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at < 1 || at === trimmed.length - 1) return null;

  const local = trimmed.slice(0, at);           // local part — case korunur
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (!domain.includes('.')) return null;

  // 1. Tam domain typo eşleşmesi
  const direct = DOMAIN_FIXES[domain];
  if (direct && direct !== domain) {
    return `${local}@${direct}`;
  }

  // 2. TLD typo (.con/.cm/.comm/.co → .com) — sadece popüler domain'lerde
  const tldFixed = domain.replace(/\.(con|cm|comm|co|cpm|vom)$/, '.com');
  if (tldFixed !== domain && POPULAR_DOMAINS.has(tldFixed)) {
    return `${local}@${tldFixed}`;
  }

  return null;
}
